import { Button, Flex, ProfileInfoCard, SlidingPanel } from '@components';
import { Box, Grid, useTheme } from '@mui/material';
import { ChangeEvent, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Container } from './BasicCustomerInfo.styles';
import {
  SliderComponents,
  SliderTypes,
  getPersonalInfo,
} from './BasicCustomerInfo.utils';
import { NomineeCustomer } from '@features/api/response/NomineeCustomer';
import BasicInfoHeader from '@components/BasicInfoHeader/BasicInfoHeader';
import NewItemDialog from '@pages/NomineeCustomers/NewCustomerForm/NewItemDialog';
import { useUpdateNomineeCustomerMutation } from '@features/api/nomineeCustomerSlice';

export interface BasicCustomerInfoProps {
  customer?: NomineeCustomer;
}

const PERSONAL_DATA_CATEGORY = 'Personal Data';

const BasicCustomerInfo = ({ customer }: BasicCustomerInfoProps) => {
  const intl = useIntl();
  const theme = useTheme();

  const [updateMutation] = useUpdateNomineeCustomerMutation();

  const [newCategoryName, setNewCategoryName] = useState<string>();
  const [showCategoryForm, setShowCategoryForm] = useState(false);

  const [sliderType, setSliderType] = useState<string>();

  const onCloseSlider = (): void => {
    setSliderType(undefined);
  };

  const handleAddCategory = () => {
    setShowCategoryForm(true);
  };

  const handleCancelCreateCategory = () => {
    setShowCategoryForm(false);
  };

  const handleNewCategoryNameChange = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    setNewCategoryName(event.target.value);
  };

  const handleCreateCategory = async () => {
    if (!customer || !newCategoryName) return;

    const updatedCategorizedData = [...customer.categorizedData];
    updatedCategorizedData.push({
      name: newCategoryName,
      fields: [],
    });

    await updateMutation({
      id: customer.id,
      firstName: customer.firstName,
      lastName: customer.lastName,
      categorizedData: [...updatedCategorizedData],
    }).unwrap();

    setShowCategoryForm(false);
  };

  if (!customer) return <></>;
  return (
    <Flex flex={1} my={14} gap={14} flexDirection='column'>
      <Box>
        <BasicInfoHeader
          user={{
            basicDetails: {
              firstName: customer.firstName,
              lastName: customer.lastName,
            },
          }}
        />
      </Box>
      <Grid
        container
        rowSpacing={10}
        columnSpacing={{ sm: 16, md: 12 }}
        paddingX={12}
        paddingY={10}
      >
        <Container>
          <Flex flex='32%' maxWidth='32%'>
            <ProfileInfoCard
              title={intl.formatMessage({
                id: 'crm.customer.basicDetails.personalDetails',
              })}
              data={getPersonalInfo(customer, theme)}
              onRightButtonPress={() =>
                setSliderType(SliderTypes.EditPersonalDetails)
              }
              sx={{ flex: 1 }}
            />
          </Flex>
          {customer.categorizedData
            .filter((x) => x.name !== PERSONAL_DATA_CATEGORY)
            .map((data, index) => (
              <Flex flex='32%' maxWidth='32%'>
                <ProfileInfoCard
                  title={data.name}
                  data={data.fields.map((field, index) => ({
                    label: field.name,
                    value: field.value,
                  }))}
                  onRightButtonPress={() => setSliderType(data.name)}
                  sx={{ flex: 1 }}
                />
              </Flex>
            ))}
        </Container>
      </Grid>
      <Flex justifyContent='center'>
        <Button variant='secondary' onClick={handleAddCategory}>
          <FormattedMessage id='crm.customer.addCategory' />
        </Button>
      </Flex>
      <SlidingPanel
        PaperProps={{ sx: { width: { xs: '100%', md: '40%' } } }}
        anchor='right'
        title={sliderType ?? ''}
        open={!!sliderType}
        onClose={onCloseSlider}
      >
        {SliderComponents(sliderType, customer, onCloseSlider)}
      </SlidingPanel>

      <NewItemDialog
        isOpen={showCategoryForm}
        onClose={handleCancelCreateCategory}
        label={intl.formatMessage({ id: 'crm.customer.categoryName' })}
        value={newCategoryName}
        onChangeValue={handleNewCategoryNameChange}
        onSubmit={handleCreateCategory}
        onCancel={handleCancelCreateCategory}
      />
    </Flex>
  );
};

export default BasicCustomerInfo;
